import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper, 
  CircularProgress,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

const OCRFlowchart = () => {
  const { tr } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [flowchartCode, setFlowchartCode] = useState('');
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const fileInputRef = useRef(null);
  const mermaidRef = useRef(null);

  // Load mermaid script dynamically
  useEffect(() => {
    const loadMermaid = async () => {
      if (!window.mermaid) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
        script.async = true;
        script.onload = () => {
          window.mermaid.initialize({
            startOnLoad: true,
            theme: 'default',
            securityLevel: 'loose',
          });
          if (flowchartCode && mermaidRef.current) {
            renderMermaid();
          }
        };
        document.body.appendChild(script);
      } else if (flowchartCode && mermaidRef.current) {
        renderMermaid();
      }
    };
    
    loadMermaid();
  }, []);

  // Render mermaid when flowchart code changes
  useEffect(() => {
    if (flowchartCode && mermaidRef.current && window.mermaid) {
      renderMermaid();
    }
  }, [flowchartCode]);

  const renderMermaid = () => {
    try {
      // Clear previous content
      if (mermaidRef.current) {
        mermaidRef.current.innerHTML = '';
        
        // Create a container for the mermaid diagram
        const container = document.createElement('div');
        container.className = 'mermaid';
        container.textContent = flowchartCode;
        mermaidRef.current.appendChild(container);
        
        // Render the diagram
        window.mermaid.run({
          nodes: [container]
        });
      }
    } catch (err) {
      console.error('Error rendering mermaid diagram:', err);
      setError(tr('無法渲染流程圖，可能是語法錯誤', 'Unable to render the flowchart. The diagram syntax may be invalid.'));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset previous results
    setExtractedText('');
    setFlowchartCode('');
    setError(null);

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError(tr('請上傳圖片檔案 (JPG, PNG, etc.)', 'Please upload an image file (JPG, PNG, etc.).'));
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(tr('圖片大小不能超過 5MB', 'The image must be 5MB or smaller.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const resetImage = () => {
    setSelectedImage(null);
    setExtractedText('');
    setFlowchartCode('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processImage = async () => {
    if (!selectedImage) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/ocr-to-flowchart`, {
        image: selectedImage
      });

      setExtractedText(response.data.text || '');
      setFlowchartCode(response.data.flowchart || '');
      
      // Log the flowchart code for debugging
      console.log("Received flowchart code:", response.data.flowchart);
    } catch (err) {
      console.error('Error processing image:', err);
      setError(tr('處理圖片時發生錯誤。請再試一次或嘗試使用不同的圖片。', 'An error occurred while processing the image. Please try again or use a different image.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setError(tr('複製到剪貼簿失敗', 'Failed to copy to the clipboard.'));
      });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        {tr('圖片轉流程圖', 'Image to Flowchart')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5', mb: 4 }}>
        <Typography variant="body1" gutterBottom>
          {tr('上傳流程圖或手繪圖的圖片，系統將自動識別文字並生成 Mermaid 流程圖。', 'Upload an image of a flowchart or hand-drawn diagram. The text will be recognized automatically and turned into a Mermaid flowchart.')}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
        
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={triggerFileInput}
          sx={{
            bgcolor: '#5ab3af',
            '&:hover': {
              bgcolor: '#4a9e9a'
            },
            mb: 3
          }}
        >
          {tr('上傳圖片', 'Upload Image')}
        </Button>

        {selectedImage && (
          <Box sx={{ mt: 2 }}>
            <Box 
              component="img" 
              src={selectedImage} 
              alt="Selected" 
              sx={{ 
                maxWidth: '100%', 
                maxHeight: '400px',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                mb: 3
              }} 
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Button 
                variant="outlined" 
                startIcon={<DeleteIcon />}
                onClick={resetImage}
                sx={{ borderColor: '#5ab3af', color: '#5ab3af' }}
              >
                {tr('移除圖片', 'Remove Image')}
              </Button>
              
              <Button 
                variant="contained" 
                onClick={processImage}
                disabled={isProcessing}
                sx={{ 
                  bgcolor: '#5ab3af',
                  '&:hover': { bgcolor: '#4a9e9a' }
                }}
              >
                {isProcessing ? (
                  <>
                    <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
                    {tr('處理中...', 'Processing...')}
                  </>
                ) : tr('識別文字', 'Extract Text')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {extractedText && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5', mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{tr('識別文字', 'Extracted Text')}</Typography>
            <IconButton 
              onClick={() => copyToClipboard(extractedText, tr('文字已複製到剪貼簿', 'Text copied to the clipboard'))}
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography 
            variant="body2" 
            component="pre" 
            sx={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              bgcolor: '#fff',
              p: 2,
              borderRadius: 1,
              maxHeight: '200px',
              overflow: 'auto'
            }}
          >
            {extractedText || tr('未能識別任何文字', 'No text was recognized')}
          </Typography>
        </Paper>
      )}

      {flowchartCode && (
        <>
          <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{tr('Mermaid 流程圖代碼', 'Mermaid Flowchart Code')}</Typography>
              <IconButton 
                onClick={() => copyToClipboard(flowchartCode, tr('Mermaid 代碼已複製到剪貼簿', 'Mermaid code copied to the clipboard'))}
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography 
              variant="body2" 
              component="pre" 
              sx={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                bgcolor: '#fff',
                p: 2,
                borderRadius: 1,
                maxHeight: '200px',
                overflow: 'auto',
                fontFamily: 'monospace'
              }}
            >
              {flowchartCode}
            </Typography>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>{tr('流程圖預覽', 'Flowchart Preview')}</Typography>
            <Box 
              ref={mermaidRef} 
              sx={{ 
                bgcolor: '#fff',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                minHeight: '200px'
              }}
            />
            
            {/* Fallback preview if mermaid fails to render */}
            {flowchartCode && !mermaidRef.current?.querySelector('.mermaid svg') && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => window.open(`https://mermaid.live/edit#pako:${btoa(flowchartCode)}`, '_blank')}
                  sx={{ mt: 2 }}
                >
                  {tr('在 Mermaid Live Editor 中查看', 'View in Mermaid Live Editor')}
                </Button>
              </Box>
            )}
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default OCRFlowchart; 